import { Injectable, inject } from '@angular/core';

import type { Observable } from 'rxjs';
import { map } from 'rxjs';

import { HttpApiService } from '@core/http-legacy';
import type { PaginationMeta } from '@core/http-types';
import type { Location, LocationsScope } from '@core/locations-types';
import type { ID } from '@core/ngrx-legacy';
import { getOptions } from '@core/walkthrough-util';

import type {
  LocationCreatePayload,
  LocationResponse,
  LocationsBriefResponse,
  LocationsPayloadParams,
  LocationsResponse,
} from '../location.types';

@Injectable({ providedIn: 'root' })
export class LocationsHttpClient {
  protected readonly httpApi = inject(HttpApiService);

  public fetchBriefCollection(page: number): Observable<LocationsBriefResponse> {
    return this.httpApi.get('locations', { page });
  }

  public fetchPaginatedCollection(
    scope: LocationsScope,
    params: LocationsPayloadParams,
  ): Observable<{ rows: Location[]; meta: PaginationMeta }> {
    return this.httpApi
      .get('locations', { scope, ...params, viewMode: 'full' })
      .pipe(map(data => ({ meta: data.meta, rows: data.locations })));
  }

  public fetchWithMissingTranslations(langCode: string): Observable<Location[]> {
    return this.httpApi
      .get('locations', { viewMode: 'full', withMissingTranslation: langCode })
      .pipe(map(data => data.locations));
  }

  public fetchOne(id: ID): Observable<LocationResponse> {
    return this.httpApi.get(`/locations/${id}`);
  }

  public create(body: LocationCreatePayload): Observable<LocationResponse> {
    return this.httpApi.post('/locations', {}, body);
  }

  public update(body: LocationCreatePayload, id: ID): Observable<LocationResponse> {
    return this.httpApi.patch(`/locations/${id}`, {}, body);
  }

  public archive(id: ID): Observable<LocationResponse> {
    return this.httpApi.patch(`/locations/${id}/archive`);
  }

  public retrieve(id: ID): Observable<LocationResponse> {
    return this.httpApi.patch(`/locations/${id}/retrieve`);
  }

  public duplicate(id: ID): Observable<LocationResponse> {
    return this.httpApi.patch(`/locations/${id}/duplicate`);
  }

  public delete(id: ID): Observable<LocationResponse> {
    return this.httpApi.delete(`/locations/${id}`);
  }

  public fetchAssignedLocationsToOffer(offerId: ID): Observable<LocationsResponse> {
    return this.httpApi.get(
      `offers/${offerId}/locations`,
      null,
      getOptions({ hasOnboarding: true }),
    );
  }

  public fetchAssignedLocationsToOfferTemplate(offerTemplateId: ID): Observable<LocationsResponse> {
    return this.httpApi.get(
      `offer_templates/${offerTemplateId}/locations`,
      null,
      getOptions({ hasOnboarding: true }),
    );
  }
}
